import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ShareIcon from '@atlaskit/icon/core/share';
import type { Appearance } from '@atlaskit/button';
import Aktooltip from '@atlaskit/tooltip';
import type { TriggerProps } from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';
import type { NewCoreIconProps } from '@atlaskit/icon';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import { PopupTrigger } from '@atlassian/entry-point-popup';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';

import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import type { ShareClient, ShareData, ShareDialogContainerProps } from '../common/types.tsx';

import { CHANNEL_ID, shareTriggerButtonClicked } from '../common/analytics.tsx';

import { shareDialogContentEntryPoint } from '../entrypoint.tsx';
import { ShareServiceClient } from '../services/ShareServiceClient.tsx';
import { EntrypointPropsProvider } from '../services/EntrypointPropsProvider.tsx';
import { useShareConfig } from '../common/utils/useShareConfig.tsx';
import { defaultShareContentState } from '../common/constants.tsx';
import { LoadingSpinner } from '../common/ui/LoadingSpinner.tsx';
import { useFetchSettings } from '../services/useFetchSettings.tsx';
import { useFetchExtendedDialogData } from '../services/useFetchExtendedDialogData.tsx';
import messages from './messages.tsx';
import { ShareButton } from './ShareButton.tsx';

export const IconShare = () => <ShareIcon spacing="spacious" label="" color="currentColor" />;

const params = {};

export const ShareDialogContainer = ({
	shareLink,
	originTracingFactory,
	productId,
	shareTitle,
	workspaceAri,
	onSubmit,
	onDialogOpen,
	formatCopyLink,
	cloudId,
	orgId,
	loadUserOptions,
	renderCustomTriggerButton,
	dialogPlacement = 'bottom-end',
	customTriggerButtonIcon,
	shareContentType,
	shareContentSubType,
	shareContentId,
	shareFormTitle,
	shareFormHelperMessage,
	showFlags,
	enableSmartUserPicker,
	loggedInAccountId,
	triggerButtonAppearance,
	triggerButtonIcon,
	triggerButtonStyle = 'icon-only',
	triggerButtonTooltipText,
	triggerButtonTooltipPosition,
	bottomMessage,
	shareeAction,
	productAttributes,
	dialogZIndex = layers.modal(),
	customHeader,
	customFooter,
	onTriggerButtonClick,
	onUserSelectionChange,
	shareFieldsFooter,
	isPublicLink,
	integrationMode,
	shareIntegrations,
	additionalTabs,
	builtInTabContentWidth,
	shareAri,
	copyTooltipText,
	onDialogClose,
	isBrowseUsersDisabled,
	userPickerOptions,
	isSubmitShareDisabled,
	isExtendedShareDialogEnabled,
	CustomSubmitButton,
	shareClient: shareClientProp,
	isAutoOpenDialog,
	dialogOffset,
}: ShareDialogContainerProps) => {
	const [defaultData, setDefaultData] = useState<ShareData>(defaultShareContentState);

	const { formatMessage } = useIntl();

	const shareClient = useMemo<ShareClient>(
		() => shareClientProp || new ShareServiceClient(),
		[shareClientProp],
	);
	const { fetch: fetchSettings, result: settings } = useFetchSettings();
	const { fetch: fetchExtendedDialogData, status: extendedDialogData } =
		useFetchExtendedDialogData();
	const { config, fetchConfig } = useShareConfig(shareClient, cloudId);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onTriggerClick = useCallback(() => {
		createAnalyticsEvent(shareTriggerButtonClicked()).fire(CHANNEL_ID);
		onTriggerButtonClick?.();
		fetchSettings();
	}, [createAnalyticsEvent, fetchSettings, onTriggerButtonClick]);

	const onFetchFormData = useCallback(() => {
		fetchConfig();
		if (isExtendedShareDialogEnabled) {
			fetchExtendedDialogData();
		}
	}, [fetchConfig, isExtendedShareDialogEnabled, fetchExtendedDialogData]);

	const localRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		/* This is a temporary solution to move away from EntrypointContainer loading.

		 */
		if (isAutoOpenDialog) {
			localRef.current?.click();
		}
	}, [isAutoOpenDialog]);

	const renderTriggerButton = useCallback(
		({ ref: triggerRef, ...triggerProps }: TriggerProps) => {
			const ref = mergeRefs(localRef, triggerRef);
			let button: React.ReactNode;
			const ShareButtonIcon: React.ComponentType<NewCoreIconProps> = triggerButtonIcon || IconShare;

			if (renderCustomTriggerButton) {
				button = renderCustomTriggerButton(
					{
						isSelected: triggerProps['aria-expanded'],
						onClick: onTriggerClick,
						iconBefore: customTriggerButtonIcon,
						isDisabled: config === 'fetching',
					},
					{
						...triggerProps,
						ref,
					},
				);
			} else {
				button = (
					<ShareButton
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						appearance={triggerButtonAppearance as Appearance}
						text={
							triggerButtonStyle !== 'icon-only'
								? formatMessage(messages.shareTriggerButtonText)
								: null
						}
						aria-label={formatMessage(messages.shareTriggerButtonText)}
						onClick={onTriggerClick}
						iconBefore={
							triggerButtonStyle !== 'text-only' ? <ShareButtonIcon label="" /> : undefined
						}
						isSelected={triggerProps['aria-expanded']}
						ref={ref}
						isDisabled={config === 'fetching'}
					/>
				);
			}
			if (triggerButtonStyle === 'icon-only') {
				button = (
					<Aktooltip
						content={
							triggerButtonTooltipText || formatMessage(messages.shareTriggerButtonTooltipText)
						}
						position={triggerButtonTooltipPosition}
						hideTooltipOnClick
					>
						{button}
					</Aktooltip>
				);
			}
			return button;
		},
		[
			config,
			customTriggerButtonIcon,
			formatMessage,
			onTriggerClick,
			renderCustomTriggerButton,
			triggerButtonAppearance,
			triggerButtonIcon,
			triggerButtonStyle,
			triggerButtonTooltipPosition,
			triggerButtonTooltipText,
		],
	);

	return (
		<ErrorBoundary id="share-dialog-container" packageName="jiraShareDialog">
			<EntrypointPropsProvider
				formSettings={{ isBrowseUsersDisabled, extendedDialogData }}
				config={config}
				emailSettings={settings}
			>
				<PopupTrigger
					entryPoint={shareDialogContentEntryPoint}
					entryPointParams={params}
					entryPointProps={{
						onFetchFormData,
						onDialogOpen,
						onDialogClose,
						onUserSelectionChange,
						onSubmit,
						showFlags,
						isExtendedShareDialogEnabled,
						shareContentType,
						shareContentSubType,
						shareContentId,
						isPublicLink,
						shareAri,
						productAttributes,
						loggedInAccountId,
						integrationMode,
						shareIntegrations,
						customFooter,
						loadUserOptions,
						shareFormTitle,
						shareFormHelperMessage,
						bottomMessage,
						shareeAction,
						customHeader,
						enableSmartUserPicker,
						cloudId,
						orgId,
						copyTooltipText,
						additionalTabs,
						builtInTabContentWidth,
						userPickerOptions,
						isSubmitShareDisabled,
						CustomSubmitButton,
						defaultData,
						setDefaultData,
						shareClient,
						shareLink,
						productId,
						originTracingFactory,
						formatCopyLink,
						shareTitle,
						workspaceAri,
						dialogZIndex,
						shareFieldsFooter,
					}}
					trigger={renderTriggerButton}
					fallback={<LoadingSpinner />}
					onError={(props) => {
						fireErrorAnalytics({
							...props,
							meta: {
								id: 'jira-share-dialog-container',
								packageName: 'jiraShareDialog',
								teamName: 'jira-warepil',
							},

							sendToPrivacyUnsafeSplunk: true,
						});
					}}
					placement={dialogPlacement}
					offset={dialogOffset}
					role="dialog"
					zIndex={dialogZIndex}
					label={formatMessage(messages.sharePopupLabel)}
				/>
			</EntrypointPropsProvider>
		</ErrorBoundary>
	);
};
